/**
 * Contact Form 7 markup kept from WordPress, submitted to /api/leads/submit.
 * Reproduces the CF7 states (classes, messages, "wpcf7mailsent" event) the theme styles and analytics rely on.
 */
const MSG = {
  sent: 'Спасибо за Ваше сообщение. Оно успешно отправлено.',
  invalid: 'Одно или несколько полей содержат ошибочные данные. Пожалуйста, проверьте их и попробуйте ещё раз.',
  failed: 'При отправке сообщения произошла ошибка. Пожалуйста, попробуйте ещё раз позже.',
  required: 'Заполните это поле.',
  email: 'Неверно введён адрес электронной почты.',
  tel: 'Номер телефона введён неверно.',
  acceptance: 'Вы должны принять условия и положения перед отправкой вашего сообщения.',
}

type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

const setStatus = (form: HTMLFormElement, status: string) => {
  form.classList.remove('init', 'submitting', 'sent', 'invalid', 'failed', 'spam', 'resetting', 'aborted')
  form.classList.add(status)
  form.dataset.status = status
}

const tip = (field: Field, message: string) => {
  const wrap = field.closest('.wpcf7-form-control-wrap') ?? field.parentElement!
  field.classList.add('wpcf7-not-valid')
  field.setAttribute('aria-invalid', 'true')
  const span = document.createElement('span')
  span.className = 'wpcf7-not-valid-tip'
  span.setAttribute('aria-hidden', 'true')
  span.textContent = message
  wrap.appendChild(span)
}

const clearTips = (form: HTMLFormElement) => {
  form.querySelectorAll('.wpcf7-not-valid-tip').forEach((t) => t.remove())
  form.querySelectorAll('.wpcf7-not-valid').forEach((f) => {
    f.classList.remove('wpcf7-not-valid')
    f.setAttribute('aria-invalid', 'false')
  })
}

const validate = (form: HTMLFormElement) => {
  let ok = true
  form.querySelectorAll<Field>('input, textarea, select').forEach((f) => {
    const v = 'value' in f ? String(f.value).trim() : ''
    const cls = f.classList
    if (f instanceof HTMLInputElement && f.type === 'checkbox') {
      if (cls.contains('wpcf7-validates-as-required') || f.closest('.wpcf7-acceptance')) {
        const group = f.closest('.wpcf7-form-control-wrap')
        const checked = group ? group.querySelector('input:checked') : f.checked
        if (!checked) {
          tip(f, f.closest('.wpcf7-acceptance') ? MSG.acceptance : MSG.required)
          ok = false
        }
      }
      return
    }
    if (cls.contains('wpcf7-validates-as-required') && !v) {
      tip(f, MSG.required)
      ok = false
    } else if (v && cls.contains('wpcf7-validates-as-email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      tip(f, MSG.email)
      ok = false
    } else if (v && cls.contains('wpcf7-validates-as-tel') && !/^[+]?[0-9() -]*[0-9][0-9() -]*$/.test(v)) {
      tip(f, MSG.tel)
      ok = false
    }
  })
  return ok
}

const formTitle = (form: HTMLFormElement) => {
  const container = form.closest('.feedback_form, .contact_form, .bron_form, .sell_form, .ord_form, section, div') ?? form
  const heading = form.querySelector('h2, h3, h4, .size_form') ?? container.querySelector('h2, h3, h4, .size_form')
  return (heading?.textContent ?? '').trim().slice(0, 300)
}

const collect = (form: HTMLFormElement) => {
  const fields: Record<string, string> = {}
  const data = new FormData(form)
  for (const [key, value] of data.entries()) {
    if (key.startsWith('_wpcf7') || key === 'hp_ufinest' || typeof value !== 'string') continue
    const name = key.replace(/\[\]$/, '')
    fields[name] = fields[name] ? `${fields[name]}, ${value}` : value
  }
  return fields
}

export async function submitCf7(form: HTMLFormElement) {
  const output = form.querySelector('.wpcf7-response-output')
  clearTips(form)
  if (!validate(form)) {
    setStatus(form, 'invalid')
    if (output) output.textContent = MSG.invalid
    return
  }
  setStatus(form, 'submitting')
  const wrapper = form.closest('.wpcf7') ?? form
  const fields = collect(form)
  try {
    const res = await fetch('/api/leads/submit', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        formId: (form.querySelector('input[name="_wpcf7"]') as HTMLInputElement | null)?.value,
        formTitle: formTitle(form),
        pageUrl: location.href,
        pageTitle: fields['page-title'] || document.title,
        fields,
        hp: (form.querySelector('input[name="hp_ufinest"]') as HTMLInputElement | null)?.value,
      }),
    })
    const body = await res.json().catch(() => ({}))
    if (res.ok && body.status === 'mail_sent') {
      setStatus(form, 'sent')
      if (output) output.textContent = body.message || MSG.sent
      form.reset()
      wrapper.dispatchEvent(new CustomEvent('wpcf7mailsent', { bubbles: true, detail: { formId: fields._wpcf7 } }))
    } else {
      setStatus(form, body.status === 'validation_failed' ? 'invalid' : 'failed')
      if (output) output.textContent = body.message || MSG.failed
    }
  } catch {
    setStatus(form, 'failed')
    if (output) output.textContent = MSG.failed
  }
}

export function initForms() {
  document.querySelectorAll<HTMLElement>('.wpcf7.no-js').forEach((w) => w.classList.replace('no-js', 'js'))
  document.querySelectorAll<HTMLFormElement>('form.wpcf7-form').forEach((form) => {
    // Honeypot: real visitors never see or fill it.
    if (!form.querySelector('input[name="hp_ufinest"]')) {
      const hp = document.createElement('input')
      hp.type = 'text'
      hp.name = 'hp_ufinest'
      hp.tabIndex = -1
      hp.autocomplete = 'off'
      hp.setAttribute('aria-hidden', 'true')
      hp.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;opacity:0'
      form.appendChild(hp)
    }
    const submit = form.querySelector('.wpcf7-submit')
    if (submit && !submit.nextElementSibling?.classList.contains('wpcf7-spinner')) {
      const spinner = document.createElement('span')
      spinner.className = 'wpcf7-spinner'
      submit.after(spinner)
    }
  })
  document.addEventListener('submit', (e) => {
    const form = e.target as HTMLFormElement
    if (!form.matches?.('form.wpcf7-form')) return
    e.preventDefault()
    void submitCf7(form)
  })
}
